import React, { useState, useEffect, memo } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import Header from "./Includes/Header";
import DashboardHeader from "./Includes/DashboardHeader";
import AdminHeader from "./Includes/AdminHeader";
import Footer from "./Includes/Footer";

// Mobile breakpoint hook
const useIsMobile = (bp = 768) => {
  const [m, setM] = React.useState(() => window.innerWidth <= bp);
  useEffect(() => {
    const h = () => setM(window.innerWidth <= bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return m;
};

const API = process.env.REACT_APP_API_URL || "";

// ── Design tokens ─────────────────────────────────────────────────────────────
const DARK   = "#1a3c2e";
const GOLD   = "#c9a84c";
const BORDER = "#e5e7eb";
const MUTED  = "#6b7280";
const BG     = "#f4f6f4";

const PLACEHOLDER_IMG = "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=800&q=80";

const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"});
};

// ── Article Detail Modal ──────────────────────────────────────────────────────
const ArticleModal = ({ article, onClose }) => {
    const [imgError, setImgError] = useState(false);
    const imgSrc = (!imgError && article.imageUrl) ? article.imageUrl : PLACEHOLDER_IMG;

    // Close on backdrop click or Escape key
    useEffect(() => {
        const handleKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", handleKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", handleKey);
            document.body.style.overflow = "";
        };
    }, [onClose]);

    return (
        <div
            onClick={onClose}
            style={{
                position: "fixed", inset: 0, zIndex: 1000,
                background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)",
                display: "flex", alignItems: "flex-start", justifyContent: "center",
                padding: "40px 16px", overflowY: "auto",
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: "#fff", borderRadius: 18, maxWidth: 760, width: "100%",
                    boxShadow: "0 24px 64px rgba(0,0,0,0.18)", overflow: "hidden",
                    marginBottom: 40,
                }}
            >
                {/* Cover image */}
                <div style={{ position: "relative", height: 320, overflow: "hidden" }}>
                    <img
                        src={imgSrc}
                        onError={() => setImgError(true)}
                        alt={article.title}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    {/* Close button */}
                    <button
                        onClick={onClose}
                        style={{
                            position: "absolute", top: 16, right: 16,
                            background: "rgba(0,0,0,0.45)", border: "none", borderRadius: "50%",
                            width: 36, height: 36, cursor: "pointer", color: "#fff",
                            fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                    >✕</button>
                </div>

                {/* Content */}
                <div style={{ padding: "32px 36px 36px" }}>
                    <div style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
                        {formatDate(article.createdAt)} · {article.author || "Treasury"}
                    </div>
                    <h2 style={{ fontSize: 26, fontWeight: 900, color: DARK, margin: "0 0 16px", lineHeight: 1.3 }}>
                        {article.title}
                    </h2>
                    <p style={{ fontSize: 15, color: "#374151", lineHeight: 1.8, margin: "0 0 20px", fontStyle: "italic", borderLeft: `3px solid ${GOLD}`, paddingLeft: 16 }}>
                        {article.summary}
                    </p>

                    {/* Full body content */}
                    {article.body && article.body.trim() && (
                        <div style={{ fontSize: 15, color: "#374151", lineHeight: 1.85, whiteSpace: "pre-wrap" }}>
                            {article.body}
                        </div>
                    )}

                    {/* External link */}
                    {article.externalUrl && (
                        <a
                            href={article.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: "inline-flex", alignItems: "center", gap: 6,
                                marginTop: 24, color: GOLD, fontWeight: 700, fontSize: 14,
                                textDecoration: "none", borderBottom: `2px solid ${GOLD}`, paddingBottom: 2,
                            }}
                        >
                            View Original Source →
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Single news card ──────────────────────────────────────────────────────────
const NewsCard = ({ article, featured, onOpen, isMobile }) => {
    const [imgError, setImgError] = useState(false);
    const imgSrc = (!imgError && article.imageUrl) ? article.imageUrl : PLACEHOLDER_IMG;
    const hasDetail = (article.body && article.body.trim()) || article.externalUrl;

    if (featured) {
        return (
            <div
                onClick={() => hasDetail && onOpen(article)}
                style={{
                    display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 0,
                    background: "#fff", borderRadius: 16, overflow: "hidden",
                    boxShadow: "0 2px 16px rgba(0,0,0,0.08)", marginBottom: 32,
                    cursor: hasDetail ? "pointer" : "default",
                    transition: "box-shadow 0.18s",
                }}
                onMouseEnter={(e) => { if (hasDetail) e.currentTarget.style.boxShadow = "0 6px 28px rgba(0,0,0,0.13)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 16px rgba(0,0,0,0.08)"; }}
            >
                <div style={{ position: "relative", minHeight: 320 }}>
                    <img
                        src={imgSrc}
                        onError={() => setImgError(true)}
                        alt={article.title}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    <span style={{
                        position: "absolute", top: 16, left: 16,
                        background: GOLD, color: "#fff", fontSize: 11, fontWeight: 800,
                        padding: "4px 12px", borderRadius: 20, letterSpacing: "0.06em", textTransform: "uppercase",
                    }}>Featured</span>
                </div>
                <div style={{ padding: "36px 32px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    <div style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
                        {formatDate(article.createdAt)} · {article.author || "Treasury"}
                    </div>
                    <h2 style={{ fontSize: 22, fontWeight: 800, color: DARK, margin: "0 0 14px", lineHeight: 1.3 }}>
                        {article.title}
                    </h2>
                    <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.7, margin: "0 0 20px" }}>
                        {article.summary}
                    </p>
                    {hasDetail && (
                        <span style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            color: GOLD, fontWeight: 700, fontSize: 13,
                        }}>
                            Read Full Article →
                        </span>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            onClick={() => hasDetail && onOpen(article)}
            style={{
                background: "#fff", borderRadius: 12, overflow: "hidden",
                boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
                display: "flex", flexDirection: "column",
                cursor: hasDetail ? "pointer" : "default",
                transition: "box-shadow 0.18s, transform 0.18s",
            }}
            onMouseEnter={(e) => { if (hasDetail) { e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.1)"; e.currentTarget.style.transform = "translateY(-2px)"; } }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 8px rgba(0,0,0,0.06)"; e.currentTarget.style.transform = "none"; }}
        >
            <div style={{ height: 180, overflow: "hidden", flexShrink: 0 }}>
                <img
                    src={imgSrc}
                    onError={() => setImgError(true)}
                    alt={article.title}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
            </div>
            <div style={{ padding: "18px 20px", flex: 1, display: "flex", flexDirection: "column" }}>
                <div style={{ fontSize: 11, color: MUTED, marginBottom: 8 }}>
                    {formatDate(article.createdAt)} · {article.author || "Treasury"}
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: DARK, margin: "0 0 10px", lineHeight: 1.4 }}>
                    {article.title}
                </h3>
                <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.65, margin: "0 0 16px", flex: 1 }}>
                    {article.summary}
                </p>
                {hasDetail && (
                    <span style={{ color: GOLD, fontWeight: 700, fontSize: 12 }}>
                        Read More →
                    </span>
                )}
            </div>
        </div>
    );
};

// ── Main News page ────────────────────────────────────────────────────────────
const News = () => {
    const { auth } = useSelector((state) => state);
    const isMobile = useIsMobile();
    const [header, setHeader] = useState("guest");
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedArticle, setSelectedArticle] = useState(null);

    useEffect(() => {
        if (auth.data) {
            const response = auth.data;
            if (response.status === 1 && response.user) {
                setHeader(response.user.role !== 1 ? "dashboard" : "admin");
            } else {
                setHeader("guest");
            }
        }
    }, [auth]);

    useEffect(() => {
        setLoading(true);
        axios.get(`${API}/api/news/public`)
            .then((res) => {
                setArticles(res.data?.data || []);
                setError(null);
            })
            .catch(() => setError("Failed to load news. Please try again later."))
            .finally(() => setLoading(false));
    }, []);

    const featured = articles[0] || null;
    const rest = articles.slice(1);

    return (
        <div style={{ background: BG, minHeight: "100vh" }}>
            {header === "guest" ? <Header /> : header === "dashboard" ? <DashboardHeader /> : <AdminHeader />}

            <div style={{ maxWidth: 1100, margin: "0 auto", padding: "48px 24px" }}>
                {/* Page header */}
                <div style={{ marginBottom: 36 }}>
                    <h1 style={{ fontSize: 32, fontWeight: 900, color: DARK, margin: "0 0 8px" }}>News</h1>
                    <p style={{ fontSize: 15, color: MUTED, margin: 0 }}>
                        Latest updates, announcements, and insights from Treasury.
                    </p>
                </div>

                {loading && (
                    <div style={{ textAlign: "center", padding: "60px 0", color: MUTED, fontSize: 15 }}>
                        Loading news…
                    </div>
                )}

                {!loading && error && (
                    <div style={{ textAlign: "center", padding: "60px 0", color: "#dc2626", fontSize: 15 }}>
                        {error}
                    </div>
                )}

                {!loading && !error && articles.length === 0 && (
                    <div style={{
                        textAlign: "center", padding: "80px 0",
                        background: "#fff", borderRadius: 16, border: `1px solid ${BORDER}`,
                    }}>
                        <div style={{ fontSize: 40, marginBottom: 16 }}>📰</div>
                        <h3 style={{ fontSize: 18, fontWeight: 700, color: DARK, margin: "0 0 8px" }}>No news yet</h3>
                        <p style={{ fontSize: 14, color: MUTED, margin: 0 }}>
                            Check back soon for the latest updates from Treasury.
                        </p>
                    </div>
                )}

                {!loading && !error && featured && (
                    <NewsCard article={featured} featured onOpen={setSelectedArticle} isMobile={isMobile} />
                )}

                {!loading && !error && rest.length > 0 && (
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                        gap: 24,
                    }}>
                        {rest.map((article) => (
                            <NewsCard key={article._id} article={article} onOpen={setSelectedArticle} isMobile={isMobile} />
                        ))}
                    </div>
                )}
            </div>

            <Footer />

            {/* Article detail modal */}
            {selectedArticle && (
                <ArticleModal article={selectedArticle} onClose={() => setSelectedArticle(null)} />
            )}
        </div>
    );
};

export default memo(News);
